import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
// Replace this with your actual event / tech stage image asset
import eventImg from "../../assets/images/evernt_here.jpg";

export default function EventAbout() {
  return (
    <section className="relative w-full bg-[#F8FAFC] lg:py-24 md:py-16 py-10 px-6 sm:px-8 lg:px-12 border-b border-slate-200/80 font-sans overflow-hidden">
      <div className="xl:w-4/5 mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 space-y-4">
            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 px-0.5 py-1.5 backdrop-blur-md shadow-xs"
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                WHY ATTEND &amp; WHAT TO EXPECT
              </span>
            </motion.div>
            {/* Title */}
            <div className="space-y-4">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-950 leading-tight">
                Architecting Autonomy for <br />
                <span className="italic font-serif font-normal text-[#5dc9f0]">
                  High-Throughput Scale
                </span>
              </h2>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
                Enterprise AI automation is moving past isolated prompt tests
                into resilient, mission-critical systems. Yet engineering teams
                still encounter systemic bottlenecks—non-deterministic execution,
                cascading failures, and brittle legacy integrations.
              </p>

              <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
                Our masterclasses, technical labs, and roundtables focus strictly
                on <strong>battle-tested architectural blueprints</strong>.
                Learn how engineering groups leverage{" "}
                <strong>event-driven orchestration</strong>, deterministic DMN
                safeguards, and micro-worker topologies to deploy self-healing,
                production-ready AI agents with zero sales fluff.
              </p>
            </div>

            {/* Core Capabilities Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
              {[
                "Production-grade code & repo walk-throughs",
                "Fault-tolerant worker isolation strategies",
                "Interactive live Q&A with principal architects",
                "Downloadable blueprints & DMN matrices",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 text-xs sm:text-sm font-medium text-slate-800"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#5dc9f0] shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Image with Floating Metadata Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative flex">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full h-[520px] lg:h-full min-h-[480px] rounded-xl overflow-hidden bg-[#000036] shadow-[0_25px_60px_rgba(0,0,0,0.08)] border border-black/10 flex flex-col justify-between p-8 sm:p-8"
              >
                <img
                  src={eventImg}
                  alt="Enterprise AI Summit & Architecture Lab"
                  className="absolute inset-0 h-full w-full object-cover grayscale-[15%] contrast-110 opacity-70"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#000036] via-[#000036]/10 to-[#000036]/20" />
                <div className="relative z-10 flex items-center justify-between"></div>

                <div className="relative z-10 p-4 rounded-xl bg-[#000036]/90 backdrop-blur-xl border border-white/10 shadow-md">
                  <h4 className="font-display text-base font-bold text-white leading-snug">
                    Live System Demonstrations
                  </h4>
                  <p className="mt-1 text-sm text-slate-200 font-normal leading-relaxed">
                    Witness live Zeebe cluster failover, sub-second inference
                    routing, and end-to-end orchestration benchmarks.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}